import moment from 'moment';
import {
  ENUM_BANK_NAMES,
  ENUM_EVENT_FILTER_LIST,
  ENUM_NOTIFICATION_FILTERS,
  ENUM_ONBOARDING_STATUS,
  ENUM_ONBOARDING_STEP,
  ENUM_ORDER_PROGRESS,
  ENUM_ORDER_STATUS
} from './enum';
import validator from 'validator';
import { TimeSelectorType } from 'components/base/time-selector/types';
import { BrandType } from 'models/brand.model';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import { EventReportType } from 'pages/payment/events-table/types';
import { UserType } from 'models/user.model';
import { AmPmType } from '@cokitchen/cokitchen-components/dist/date-range-selector/types';
import { CalculatedOrderType, OrderMealType, OrderType } from 'models/order.model';
import { CalculatedOrderMealType, MealItemType } from 'models/meal.model';
import { MemberStatementType, StatementType } from 'models/statements.model';
import { EventParamsType } from 'models/event.model';
import { OnboardingType } from 'models/company.model';

export const getBankLogo = (bankName: ENUM_BANK_NAMES) => {
  let logo_url;
  if (bankName === ENUM_BANK_NAMES['9PSB']) {
    logo_url =
      'https://fc-storage-e4fedsbpcfd8ajb9.z01.azurefd.net/upload-service/1685545971591f6f886df-ad3e-4237-9175-de3bea9dbe96';
  } else if (bankName === ENUM_BANK_NAMES.ACCESS) {
    logo_url =
      'https://fc-storage-e4fedsbpcfd8ajb9.z01.azurefd.net/upload-service/16855462250115d4895d1-eda3-4838-bbb1-9c6a6765e51a';
  } else if (bankName === ENUM_BANK_NAMES.FCMB) {
    logo_url =
      'https://fc-storage-e4fedsbpcfd8ajb9.z01.azurefd.net/upload-service/1685546344704e6def974-3104-4d2a-b939-d2348920218b';
  } else if (bankName === ENUM_BANK_NAMES.GT) {
    logo_url =
      'https://fc-storage-e4fedsbpcfd8ajb9.z01.azurefd.net/upload-service/168554625311341b615fc-d7f4-436c-8b1e-2692ac2c4b65';
  } else if (bankName === ENUM_BANK_NAMES.HERITAGE) {
    logo_url =
      'https://fc-storage-e4fedsbpcfd8ajb9.z01.azurefd.net/upload-service/1685546365023459912c8-0de4-4c9f-8f19-0c17bf180115';
  } else if (bankName === ENUM_BANK_NAMES.PROVIDUS) {
    logo_url =
      'https://fc-storage-e4fedsbpcfd8ajb9.z01.azurefd.net/upload-service/168554618629907c1d4b1-36af-4bac-b3d6-ecf7424bb00a';
  } else if (bankName === ENUM_BANK_NAMES.STANBIC) {
    logo_url =
      'https://fc-storage-e4fedsbpcfd8ajb9.z01.azurefd.net/upload-service/1685546310976e3583334-c01c-44f2-825b-5fdc97b96428';
  } else {
    logo_url =
      'https://fc-storage-e4fedsbpcfd8ajb9.z01.azurefd.net/upload-service/16855462843845e654de2-5f08-4b4e-b107-15d169c4b796';
  }
  return logo_url;
};

export const copyTextToClipboard = async (text: string, callback?: (text?: string) => void) => {
  await navigator.clipboard.writeText(text);
  callback?.(text);
};

export const formatCurrency = (
  figure: number | string,
  options?: Intl.NumberFormatOptions,
  truncateFigure?: boolean
) => {
  if (!figure && +figure !== 0) return '-';
  const numericValue = +figure;

  if (truncateFigure && numericValue >= 10000000) {
    const formattedValue = (numericValue / 1000000).toFixed(1);
    return formattedValue.endsWith('.0') ? formattedValue.slice(0, -2) + 'M' : formattedValue + 'M';
  }

  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 2,
    ...options
  }).format(numericValue);
};

export const formatDate = (timeStr: string | Date, options?: Intl.DateTimeFormatOptions) => {
  if (!timeStr) return '-';
  // add default styles here
  let styleObj: Intl.DateTimeFormatOptions = {};

  if (options) styleObj = { ...styleObj, ...options };
  return new Intl.DateTimeFormat('en-NG', styleObj).format(new Date(timeStr));
};
export const getDecimalValue = (figure: number | string) => {
  const arr = `${figure}`.split('.');
  return arr.length > 1 ? arr[1] : '00';
};

export const getFullName = (
  user?: { first_name: string; last_name: string },
  noValueString = '-'
): string => {
  if (!user) return noValueString;
  const { first_name, last_name } = user;

  if (!first_name) {
    return last_name;
  }
  if (!last_name) {
    return first_name;
  }
  return `${first_name} ${last_name}`;
};

export const getDurationSince = (timeObj: Date, timeSince: Date) => {
  const diffInSecs = (timeSince.getTime() - timeObj.getTime()) / 1000;
  if (diffInSecs >= 24 * 60 * 60) {
    const diff = `${diffInSecs / (24 * 60 * 60)}`;
    return `${diff.split('.')[0]} days`;
  }
  if (diffInSecs >= 60 * 60) {
    const diff = diffInSecs / (60 * 60);
    const hours = Math.floor(diff);
    const mins = `${diff}`.split('.')[1].substring(0, 2);
    return `${hours} hr ${Math.floor((+mins * 60) / 100)} min`;
  }
  if (diffInSecs >= 60) {
    const diff = diffInSecs / 60;
    const mins = Math.floor(diff);
    const secs = `${diff}`.split('.')[1].substring(0, 2);
    return `${mins} min ${Math.floor((+secs * 60) / 100)} sec`;
  }
  if (diffInSecs < 60 && diffInSecs > 0) {
    const diff = `${Math.abs(diffInSecs)}`;
    return `${diff.split('.')[0]} secs`;
  }
};

export function formatNumber(value: number | string, options?: Intl.NumberFormatOptions) {
  if (!value && value !== 0) return '-';
  let styleObj: Intl.NumberFormatOptions = {
    style: 'currency',
    currencyDisplay: 'symbol',
    currency: 'NGN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  };
  if (options) styleObj = { ...styleObj, ...options };

  return new Intl.NumberFormat('en-NG', styleObj).format(+value);
}
const isDateValid = (date: string): boolean => {
  const dateValue = new Date(date);
  return !isNaN(dateValue.getTime());
};

const getOrdinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'],
    v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const MONTHS_OF_YEAR = [
  'Janaury',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

export const getDate = (date: string): number | null | string => {
  const dateValue = new Date(date);
  const value = dateValue.getDate();
  const ordinalValue = getOrdinal(value);
  return isDateValid(date) ? ordinalValue : null;
};

export const getMonth = (date: string): string | null => {
  const dateValue = new Date(date);
  return isDateValid(date) ? MONTHS_OF_YEAR[dateValue.getMonth() ?? 0] : null;
};
export const getShortMonth = (date: string): string | null => {
  const month = getMonth(date);
  return month ? month.slice(0, 3) : null;
};

export const getYear = (date: string): number | null => {
  const dateValue = new Date(date);
  return isDateValid(date) ? dateValue.getFullYear() : null;
};

export const getSimpleDateFormat = (date: string): string | null => {
  return `${getDate(date)} ${getShortMonth(date)} ${getYear(date)}`;
};

export const getDateRange = (startDate: string, endDate: string): string | null => {
  const currentYear = new Date().getFullYear();
  if (getYear(startDate) === getYear(endDate)) {
    return `${moment(startDate).format('Do MMM, h:mm a')} - ${moment(endDate).format(
      'Do MMM, h:mm a'
    )}${getYear(endDate) === currentYear ? '' : `, ${getYear(endDate)} `}`;
  } else {
    return `${moment(startDate).format('Do MMM YYYY, h:mm a')}, ${moment(endDate).format(
      'Do MMM YYYY, h:mm a'
    )}`;
  }
};
export const dateDifference = (a: any, b: any) => {
  const c = moment(b);
  const d = moment(a);

  return c.diff(d, 'days'); // 86400000
};

export const getUserInitials = (user?: UserType) => {
  const userName = getFullName(user);
  const [firstname, lastname] = userName.split(' ');
  if (firstname && lastname) {
    return `${firstname[0].toLocaleUpperCase()}${lastname[0].toLocaleUpperCase()}`;
  }
  if (firstname) {
    return firstname[0].toLocaleUpperCase();
  }
  if (lastname) {
    return lastname[0].toLocaleUpperCase();
  }
};
export const currentMonth = moment().format('MMMM');

export const getFirstDayOfMonth = (month?: string, year?: string) => {
  if (month && year) {
    return moment()
      .month(MONTHS_OF_YEAR.indexOf(month))
      .startOf('month')
      .year(Number(year))
      .format('YYYY-MM-DD');
  } else {
    return moment().startOf('month').format('YYYY-MM-DD');
  }
};
export const getLastDayOfMonth = (month?: string, year?: string) => {
  if (month && year) {
    return moment()
      .month(MONTHS_OF_YEAR.indexOf(month))
      .endOf('month')
      .year(Number(year))
      .format('YYYY-MM-DD');
  } else {
    return moment().endOf('month').format('YYYY-MM-DD');
  }
};

export const getFirstDayOfYear = (filter?: string) => {
  if (filter) {
    return moment().startOf('year').year(Number(filter)).format('YYYY-MM-DD');
  } else {
    return moment().startOf('year').format('YYYY-MM-DD');
  }
};

export const getLastDayOfYear = (filter?: string) => {
  if (filter) {
    return moment().endOf('year').year(Number(filter)).format('YYYY-MM-DD');
  } else {
    return moment().endOf('year').format('YYYY-MM-DD');
  }
};

export const getTimeDuration = (durationInSeconds: number) => {
  const hours = Math.floor(durationInSeconds / 3600);
  const minutes = Math.floor((durationInSeconds % 3600) / 60);
  const seconds = Math.floor(durationInSeconds % 60);
  let timeString;
  if (hours) {
    timeString = `${hours} hrs ${minutes} mins ${seconds} s`;
  } else if (minutes) {
    timeString = `${minutes} mins ${seconds} s`;
  } else {
    timeString = `${seconds} s`;
  }
  return timeString;
};

export function isValidEmail(email: string) {
  return validator.isEmail(email);
}

const getStartOfDay = (day = new Date()) => {
  return new Date(moment(day).utc(true).startOf('day').valueOf());
};

const getEndOfDay = (day = new Date()) => {
  return new Date(moment(day).utc().endOf('day').valueOf());
};

export const notificationDateRange = (dateRange: ENUM_NOTIFICATION_FILTERS) => {
  const newDate = new Date();
  let startDate: Date;
  let endDate: Date;

  if (dateRange === ENUM_NOTIFICATION_FILTERS.THIS_MONTH) {
    const firstDayOfMonth = new Date(newDate.getFullYear(), newDate.getMonth(), 1);
    const lastDayOfMonth = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0);
    startDate = getStartOfDay(firstDayOfMonth);
    endDate = getEndOfDay(lastDayOfMonth);
  } else if (dateRange === ENUM_NOTIFICATION_FILTERS.THIS_WEEK) {
    const firstDay = new Date(newDate.setDate(newDate.getDate() - newDate.getDay()));
    const lastDay = new Date(newDate.setDate(newDate.getDate() - newDate.getDay() + 6));
    startDate = getStartOfDay(firstDay);
    endDate = getEndOfDay(lastDay);
  } else if (dateRange === ENUM_NOTIFICATION_FILTERS.LAST_WEEK) {
    const currentDate = moment().utc();
    const lastSunday = currentDate.clone().day(-7);
    const previousSaturday = currentDate.clone().day(-1);
    startDate = lastSunday.startOf('day').toDate();
    endDate = previousSaturday.endOf('day').toDate();
  } else {
    // Today
    startDate = getStartOfDay();
    endDate = getEndOfDay();
  }
  return {
    startDate,
    endDate
  };
};

export const getFileSize = (size: number) => {
  let fileSize;

  if (size < 1024) {
    fileSize = size + ' bytes';
  } else if (size < 1024 * 1024) {
    fileSize = (size / 1024).toFixed(2) + ' kb';
  } else if (size < 1024 * 1024 * 1024) {
    fileSize = (size / (1024 * 1024)).toFixed(2) + ' mb';
  }

  return fileSize;
};

export const calendarDateFormat = (date: string) => {
  return moment(date).calendar(null, {
    sameDay: '[Today,] LT',
    nextDay: '[Tomorrow,] LT',
    nextWeek: 'dddd[,] LT',
    lastDay: '[Yesterday,] LT',
    lastWeek: 'dddd[,] LT',
    sameElse: 'L'
  });
};

export const getLocationOrigin = () => {
  return window?.location?.origin ?? 'https://staging-business.getfoodcourt.com';
};

export const calculateTimeRemaining = (): number => {
  const now = new Date();
  const midnight = new Date();
  midnight.setHours(24, 0, 0, 0);

  const difference = midnight.getTime() - now.getTime();
  return Math.max(0, Math.round(difference / 1000));
};

export const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const formattedHours = String(hours).padStart(2, '0');
  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(remainingSeconds).padStart(2, '0');
  return `${formattedHours}hr:${formattedMinutes}m:${formattedSeconds}s`;
};
export const formatTrackOrderTime = (timeStr: string) => {
  return Intl.DateTimeFormat('en-NG', {
    timeStyle: 'short',
    hourCycle: 'h12'
  }).format(new Date(timeStr));
};

export const changeDateTime = (dateTime: string, timeObj: TimeSelectorType) => {
  const date = new Date(dateTime);
  const { hours, minutes, amPm } = timeObj;

  // Convert 12-hour format to 24-hour format
  let hours24 = parseInt(hours ?? '', 10);
  if (amPm === 'PM' && hours24 < 12) {
    hours24 += 12;
  } else if (amPm === 'AM' && hours24 === 12) {
    hours24 = 0;
  }

  const newDate = new Date(date);
  newDate.setHours(hours24, parseInt(minutes, 10));
  const newDateWithMoment = moment(newDate).utcOffset('+01:00').format();

  return newDateWithMoment;
};

export const formatApiTimeToTimeComponentFormat = (time: string) => {
  return {
    amPm: moment(time).format('a').toUpperCase() as AmPmType,
    hours: moment(time).format('hh'),
    minutes: moment(time).format('mm'),
    timeWithColon: moment(time).format('hh:mm'),
    totalHours: '12'
  };
};

export const formatPlural = (number: number, text: string, suffix = 's'): string => {
  return `${number} ${text}${number !== 1 ? suffix : ''}`;
};
// Reducer function to compute total
export const getTotal = (items: any[]): number => {
  return items.reduce((acc: number, curr) => {
    return acc + +(curr.quantity ?? 0);
  }, 0);
};

// To calculate the total number of items bought
export const getItemsTotal = (ords: any): number => {
  const tot: number[] = [];
  for (let i = 0; i < ords?.length; i++) {
    tot.push(getTotal(ords[i].meals));
  }
  return tot?.reduce((acc: number, curr) => {
    return acc + curr;
  }, 0);
};

export const toTitleCase = (str: string | undefined) => {
  if (!str) return '';
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};

export const cleanObject = (obj: any) => {
  const newObj: any = {};
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    if (value !== '' && value !== null && value !== undefined && value?.length !== 0) {
      newObj[key] = value;
    }
  });
  return newObj;
};

export function convertTime(time12h: string) {
  if (!time12h) return '';
  const formattedTime = time12h.trim();
  const [time, modifier] = formattedTime.split(' ');
  /* eslint-disable */
  let [hours, minutes] = time.split(':');
  if (hours?.trim() === '12') {
    hours = '00';
  }

  if (modifier?.trim() === 'PM') {
    /* eslint-disable */
    hours = (parseInt(hours, 10) + 12).toString();
  }

  return `${hours}:${minutes}`;
}

export function formatDaysOfWeek(daysOfWeek: string[]) {
  if (daysOfWeek.length === 0) {
    return '';
  } else {
    return daysOfWeek
      .map((day) => {
        if (day === 'Thursday') return 'Thur';
        else {
          return day.substring(0, 3);
        }
      })
      .join(', ');
  }
}
export const convertTo12hrs = (time24hr: string) => {
  const [hours, minutes] = time24hr.split(':');
  let period = 'AM';
  let hours12 = parseInt(hours, 10);
  if (hours12 >= 12) {
    period = 'PM';
    if (hours12 > 12) {
      hours12 -= 12;
    }
  }
  if (hours12 === 0) {
    hours12 = 12;
  }
  const time12hr = `${hours12}:${minutes} ${period}`;
  return time12hr;
};

export const truncateString = (str: string, numWords: number) => {
  if (!str) return str;
  const words = str.split(' ');
  if (words?.length <= numWords) {
    return str;
  }
  if (numWords > words?.length) {
    return words?.slice(0, words?.length).join(' ') + '...';
  }
  if (numWords <= words.length) {
    return words?.slice(0, numWords).join(' ') + '...';
  }
};
export const isFutureDate = (date: string): boolean => {
  return moment(date).isAfter(moment());
};

export const getOrderStatus = (order?: OrderType) => {
  if (!order) return ENUM_ORDER_STATUS.SCHEDULED;
  const isScheduled =
    order.calculated_order?.scheduled &&
    !order.recurring &&
    isFutureDate(order.kitchen_start_order);
  const isRecurring =
    order.recurring && order.calculated_order?.scheduled && isFutureDate(order.kitchen_start_order);
  const isPending = order.preorder_delivery_time && isFutureDate(order.kitchen_start_order);
  const isOngoing = !order.completed && !order.cancelled;
  if (order.completed) return ENUM_ORDER_STATUS.COMPLETED;
  else if (order.cancelled && !order.kitchen_cancelled) return ENUM_ORDER_STATUS.CANCELLED;
  else if (order.cancelled && order.kitchen_cancelled) return ENUM_ORDER_STATUS.REJECTED;
  else if (isScheduled) return ENUM_ORDER_STATUS.SCHEDULED;
  else if (isRecurring) return ENUM_ORDER_STATUS.RECURRING;
  if (isPending) return ENUM_ORDER_STATUS.PENDING;
  else if (isOngoing) return ENUM_ORDER_STATUS.ONGOING;
  return ENUM_ORDER_STATUS.SCHEDULED;
};

export const getOrderMealsCount = (order?: OrderType) => {
  if (!order) return 0;
  return (order.calculated_order?.meals ?? []).reduce(
    (total, meal) => total + meal.meals.length,
    0
  );
};

export const formatDeliveryTime = (time: string) => {
  if (!time) return '';
  const timeArray = time.split(' - ');
  if (timeArray.length === 1) {
    const startTime = moment(timeArray?.[0], 'HH:mm').format('h:mm A');
    return `${startTime}`;
  } else {
    const startTime = moment(timeArray?.[0], 'HH:mm').format('h:mm A');
    const endTime = moment(timeArray?.[1], 'HH:mm').format('h:mm A');
    return `${startTime} - ${endTime}`;
  }
};

export const formatToSentenceCase = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const parseTime = (timeString: string): number => {
  const [hours, minutes] = timeString.split(':').map(Number);
  const adjustedHours = hours === 0 ? 23 : hours - 1;
  return adjustedHours * 60 + minutes;
};

export const isBrandOpenNow = (restaurant: BrandType): boolean => {
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
  const workingHours = restaurant.brand_working_hours?.find(
    (hours) => hours.name.toLowerCase() === today.toLowerCase()
  );
  if (workingHours) {
    const currentTime = parseTime(moment().format('HH:mm'));
    const openingTime = parseTime(workingHours.opening_time);
    const closingTime = parseTime(workingHours.closing_time);
    return openingTime <= currentTime && closingTime > currentTime;
  } else {
    return false;
  }
};

export function getAssignedUsers(meal: MealItemType) {
  if (meal.members && meal.members.length > 0) {
    switch (meal.members?.length) {
      case 1:
        return `${meal.members[0].firstName} ${meal.members[0].lastName}`;
      case 2:
        return `${meal.members[0].firstName} & ${meal.members[1].firstName}`;
      default:
        return `${meal.members[0].firstName} & Others`;
    }
  }
}
export const getCurrentStep = (order: OrderType) => {
  const {
    paid,
    kitchen_prepared,
    kitchen_accepted,
    kitchen_dispatched,
    rider_assigned,
    completed
  } = order;
  if (completed) {
    ENUM_ORDER_PROGRESS.ORDER_COMPLETED;
  } else if (kitchen_dispatched && rider_assigned) {
    ENUM_ORDER_PROGRESS.ORDER_IN_TRANSIT;
  } else if (kitchen_prepared && !rider_assigned) {
    return ENUM_ORDER_PROGRESS.AWAITING_RIDER;
  } else if (kitchen_accepted && !kitchen_prepared) {
    return ENUM_ORDER_PROGRESS.PREPARING_YOUR_ORDER;
  } else if (paid) {
    return ENUM_ORDER_PROGRESS.PAYMENT_RECEIVED;
  }
};

export const formatMealsToBulkMealFormat = (
  calculatedOrders: CalculatedOrderMealType[]
): OrderMealType[] => {
  return calculatedOrders.flatMap((calculatedOrder) =>
    calculatedOrder?.meals?.map((meal) => ({
      created_at: new Date(meal.created_at),
      id: meal.id,
      quantity: meal.quantity ?? 1,
      name: meal.name,
      brand: {
        id: calculatedOrder.brand.id.toString(),
        name: calculatedOrder.brand.name
      },
      members: meal.members || [],

      addons:
        meal.addons?.map((addon) => ({
          meal_data: addon?.meal_data,
          id: addon.id,
          name: addon?.meal_data?.name,
          quantity: addon.quantity ?? 1,
          price: addon?.meal_data?.amount,
          is_default: false
        })) || [],
      price: Number(meal.amount) || 0,
      amount: Number(meal.amount) || 0
    }))
  );
};

export const formatScheduledDeliveryDate = (dateString: string): string => {
  if (!dateString) return '';
  const currentDate = moment();
  const inputDate = moment(dateString);
  if (inputDate.isSame(currentDate, 'day')) {
    return 'today';
  }
  if (inputDate.isSame(currentDate.clone().add(1, 'day'), 'day')) {
    return 'tomorrow';
  }
  if (inputDate.isoWeek() === currentDate.isoWeek()) {
    return inputDate.format('dddd');
  }
  return inputDate.format('MMMM DD');
};

export const formatRecurringOrdersDate = (dateString: string | null): string => {
  if (!dateString) return '';
  const formattedDate = moment.utc(dateString).format('ddd, Do MMM hh:mmA');
  return formattedDate;
};

export const timeFrames = (
  date: Date,
  defaultStartDate: Date,
  defaultTime: string,
  setTime: (time: string) => void
) => {
  if (date === defaultStartDate) setTime(defaultTime);
  else setTime('');
  const isToday = moment(date).isSame(moment(), 'day');
  let startOfDay;
  if (isToday) {
    const currentMinute = moment().minute();
    startOfDay = moment()
      .add(currentMinute === 0 ? 1 : 2, 'hours')
      .startOf('hour');
  } else {
    startOfDay = moment().set('hour', 9).set('minute', 0);
  }
  const endOfDay = moment().set('hour', 18).startOf('hour');
  const times: string[] = [];
  while (startOfDay.isBefore(endOfDay)) {
    const startTime = startOfDay.format('hh:mm A');
    const endTime = startOfDay.clone().add(1, 'hours').format('h:mm A');
    times.push(`${startTime} - ${endTime}`);
    startOfDay.add(1, 'hours');
  }

  return times;
};

export const openingTime = (restaurant: BrandType) => {
  const dayOfWeek = moment().day();
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayName = days[dayOfWeek];
  const workingHours = restaurant.brand_working_hours?.find((time) => time.name === dayName);
  const openingTime = workingHours?.opening_time;
  return moment(openingTime, 'HH:mm:mm').format('hh:mm A');
};

export const closingTime = (restaurant: BrandType) => {
  const dayOfWeek = moment().day();
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayName = days[dayOfWeek];
  const workingHours = restaurant.brand_working_hours?.find((time) => time.name === dayName);
  const closingTime = workingHours?.closing_time;
  return moment(closingTime, 'HH:mm:mm').format('hh:mm A');
};
export const calculateReactivationTime = (time: string | null) => {
  if (!time) return 0;
  const currentTime = new Date().getTime() / 1000;
  const reactivationTime = time ? new Date(time).getTime() / 1000 : 0;
  const returnVal = Math.max(0, reactivationTime - currentTime);
  return Math.ceil(returnVal);
};

export const getCountdownFromSeconds = (seconds: number) => {
  if (!seconds || seconds <= 0) return '00:00:00';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  const formattedHours = hours.toString().padStart(2, '0');
  const formattedMinutes = minutes.toString().padStart(2, '0');
  const formattedSeconds = remainingSeconds.toString().padStart(2, '0');
  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};

export const getCountdownWords = (date: string | null) => {
  if (!date) return '0 secs';
  const duration = moment.duration(moment(date).diff(moment()));
  const days = duration.days();
  const hours = duration.hours();
  const minutes = duration.minutes();
  const seconds = duration.seconds();
  if (!(days || hours || minutes)) return `${seconds} ${seconds === 1 ? 'sec' : 'secs'}`;
  let countdown = '';
  if (days) countdown += ` ${days} ${days === 1 ? 'day' : 'days'}`;
  if (hours) countdown += ` ${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
  countdown += ` ${minutes} ${minutes === 1 ? 'min' : 'mins'}`;
  return countdown.trim();
};

type GetNearestCokitchenType = {
  lat: unknown;
  lng: unknown;
} & {
  [key: string]: unknown;
};

export const getNearestCokitchen = async (
  address: GetNearestCokitchenType,
  setLoading?: (loading: boolean) => void
) => {
  try {
    setLoading?.(true);
    const data = { lat: Number(address.lat), lng: Number(address.lng) };
    const res = await apiService.post(`internal/location`, {
      baseURL: process.env.REACT_APP_ORDER_API_URL,
      isInternal: true,
      data
    });
    return res.data.cokitchen;
  } finally {
    setLoading?.(false);
  }
};

type GetIsBusyType = {
  is_busy: boolean;
  reactivation_time: string | null;
} & {
  [key: string]: unknown;
};

export const getIsBusy = (data: GetIsBusyType) => {
  return data?.reactivation_time ? moment().isBefore(data?.reactivation_time) : data?.is_busy;
};

export const getCurrentEventStatus = (startDate: string, endDate: string) => {
  const currentDate = moment();
  const startDateMoment = moment(startDate);
  const endDateMoment = moment(endDate);

  if (currentDate.isBefore(startDateMoment)) {
    return 'Inactive';
  } else if (currentDate.isAfter(endDateMoment)) {
    return 'Completed';
  } else if (startDateMoment.isBefore(currentDate) && currentDate.isBefore(endDateMoment)) {
    return 'Active';
  } else return '';
};

export const getEventFilterParams = (filterValue: string, companyId: string) => {
  const params: EventParamsType = {
    company_id: companyId
  };
  if (filterValue === ENUM_EVENT_FILTER_LIST.MOST_RECENT) {
    params.recent = 'yes';
  } else if (filterValue === ENUM_EVENT_FILTER_LIST.ACTIVE) {
    params.status = 'Active';
  } else if (filterValue === ENUM_EVENT_FILTER_LIST.INACTIVE) {
    params.status = 'Inactive';
  } else if (filterValue === ENUM_EVENT_FILTER_LIST.COMPLETED) {
    params.status = 'Completed';
  } else if (filterValue === ENUM_EVENT_FILTER_LIST.LOW_TO_HIGH_BUDGET) {
    params.budget = 'low';
  } else if (filterValue === ENUM_EVENT_FILTER_LIST.HIGH_TO_LOW_BUDGET) {
    params.budget = 'high';
  }
  return params;
};

export const handleApiError = (error: unknown, fallback = 'An error occurred') => {
  const message = (error as any)?.response?.data?.message;
  const messages = Array.isArray(message) ? message : [message];
  messages.forEach((message) => {
    toast(<ToastMessage type='error' message={message || fallback} />);
  });
};

export const formatGeneralStatementCellValue = (
  col: { dataKey: string; header: string },
  row: StatementType
) => {
  switch (col.dataKey) {
    case 'created_at':
      return formatDate(row['created_at']);
    case 'amount':
      return Number(row['amount']).toLocaleString();
    case 'balance_after':
      return Number(row['balance_after']).toLocaleString();
    default:
      return row[col.dataKey as keyof StatementType];
  }
};

export const formatEventStatementCellValue = (
  col: { dataKey: string; header: string },
  row: EventReportType
) => {
  switch (col.dataKey) {
    case 'total_allocation':
      return Number(row['total_allocation']).toLocaleString();
    case 'total_spent':
      return Number(row['total_spent']).toLocaleString();
    default:
      return row[col.dataKey as keyof EventReportType];
  }
};

export const formatMemberStatementCellValue = (
  col: { dataKey: string; header: string },
  row: MemberStatementType
) => {
  switch (col.dataKey) {
    case 'total_allocation':
      return Number(row['total_allocation']).toLocaleString();
    case 'total_spent':
      return Number(row['total_spent']).toLocaleString();
    default:
      return row[col.dataKey as keyof MemberStatementType];
  }
};

export const checkForMultipleBrands = (calculatedOrder: CalculatedOrderType) => {
  if (!calculatedOrder.brand_ids) return;
  const brands = new Set<{ id: string; name: string; item_type: string; brand_type: string }>();
  if (calculatedOrder?.brand_ids?.length > 0)
    calculatedOrder?.brand_ids?.forEach((brand) => brands.add(brand));
  return brands;
};

export const getTimeRange = (timeString: string) => {
  const startTime = moment(timeString, 'HH:mm:ss');
  const endTime = startTime.clone().add(30, 'minutes');
  const range = `${startTime.format('HH:mm')} - ${endTime.format('HH:mm')}`;

  return range;
};

export const formatTimeToCompactFormat = (time: string) => {
  const momentTime = moment(time, 'HH:mm');
  if (momentTime.minutes() === 0) {
    return momentTime.format('ha');
  }
  return momentTime.format('h:mma');
};

export const convertTimeRange = (timeRange: string): string => {
  const times = timeRange.split(' - ');
  const formattedTimes = times.map((time) => formatTimeToCompactFormat(time));
  return `${formattedTimes[0]} - ${formattedTimes[1]}`;
};

export const hasTimePassed = (currentTime: string, cuttoffTime: string): boolean => {
  const currentMoment = moment(currentTime, 'h:mma');
  const cutoffMoment = moment(cuttoffTime, 'h:mma');

  return currentMoment.isAfter(cutoffMoment);
};

export const isOnboardingCompleted = (onboarding: OnboardingType[]) => {
  return onboarding.every((o) =>
    [ENUM_ONBOARDING_STATUS.COMPLETED, ENUM_ONBOARDING_STATUS.SKIPPED].includes(o.status)
  );
};

export const formatDatetime = (date: string, time: string) => {
  return moment(date).format('ddd, Do MMM') + ', ' + formatDeliveryTime(time);
};

export const completeAssignAdminStep = async (companyId: string) => {
  await apiService.put(`company/${companyId}/onboarding`, {
    data: { step: ENUM_ONBOARDING_STEP.ASSIGN_ADMIN, status: ENUM_ONBOARDING_STATUS.COMPLETED }
  });
};
